import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { Link, Navigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import SEOHead from "@/components/SEOHead";
import { caseStudies, getCaseStudy } from "./caseStudies";
import "../services/creation-site.css";
import "./case-study.css";

const CALENDLY = "https://calendly.com/hollandetorres/appel-decouverte";

export default function CaseStudy() {
  const { slug } = useParams();
  const study = getCaseStudy(slug);
  if (!study) return <Navigate to="/" replace />;

  const index = caseStudies.indexOf(study);
  const others = [1, 2, 3].map((step) => caseStudies[(index + step) % caseStudies.length]);
  const domain = study.url ? new URL(study.url).hostname.replace(/^www\./, "") : null;

  return (
    <div className="creation-site-page case-study-page min-h-screen bg-background">
      <SEOHead
        title={study.metaTitle}
        description={study.metaDescription}
        canonical={`https://htmcagency.com/realisations/${study.slug}/`}
      />

      <header className="web-header">
        <div className="container mx-auto px-6 py-6 flex items-center justify-between">
          <Link to="/" className="web-logo flex items-center" aria-label="Retour à l'accueil HTMC">
            <img src="/logo-htmc.webp" alt="HTMC Agency" className="h-14 w-auto" />
          </Link>
          <Link
            to="/#projects"
            className="flex items-center gap-2 text-white/60 hover:text-white transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" aria-hidden="true" />
            Toutes les réalisations
          </Link>
        </div>
      </header>

      <main>
        <section className="case-hero">
          <div className="container mx-auto">
            <span className="case-kicker">
              Réalisation {String(index + 1).padStart(2, "0")} / {String(caseStudies.length).padStart(2, "0")}
            </span>
            <div className="case-hero-grid">
              <div>
                <h1>{study.title}</h1>
                <p className="case-intro">{study.intro}</p>
              </div>
              <dl className="case-credits">
                <div>
                  <dt>Client</dt>
                  <dd>{study.client}</dd>
                </div>
                <div>
                  <dt>Secteur</dt>
                  <dd>{study.sector}</dd>
                </div>
                <div>
                  <dt>Lieu</dt>
                  <dd>{study.place}</dd>
                </div>
                {study.url && (
                  <div>
                    <dt>En ligne</dt>
                    <dd>
                      <a className="case-visit" href={study.url} target="_blank" rel="noopener">
                        {domain}
                        <ArrowUpRight className="w-4 h-4" aria-hidden="true" />
                      </a>
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          </div>
        </section>

        <section className="case-visual">
          <div className="container mx-auto">
            <figure className="case-browser">
              <div className="case-browser-bar" aria-hidden="true">
                <span />
                <span />
                <span />
                <em>{domain ?? study.client}</em>
              </div>
              <img
                src={study.image}
                alt={`Site web de ${study.client} réalisé par HTMC`}
                width="1800"
                height="1161"
                fetchPriority="high"
              />
            </figure>
            <dl className="case-facts">
              {study.facts.map(({ label, value }) => (
                <div key={label}>
                  <dt>{label}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
            </dl>
          </div>
        </section>

        <section className="case-need">
          <div className="container mx-auto case-columns">
            <div>
              <span className="case-label">Le point de départ</span>
              <h2>Le besoin</h2>
            </div>
            <div className="case-need-text">
              {study.challenge.map((p) => (
                <p key={p}>{p}</p>
              ))}
            </div>
          </div>
        </section>

        <section className="case-work">
          <div className="container mx-auto">
            <span className="case-label">Ce que nous avons fait</span>
            <h2>Le travail</h2>
            <ol className="case-steps">
              {study.work.map(({ title, body }, i) => (
                <li key={title}>
                  <span className="case-step-number" aria-hidden="true">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3>{title}</h3>
                  <p>{body}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {study.quote && (
          <section className="case-quote">
            <div className="container mx-auto">
              <figure>
                <blockquote lang={study.quote.lang}>{study.quote.text}</blockquote>
                <figcaption>
                  <img src={study.quote.avatar} alt="" width="52" height="52" loading="lazy" />
                  <span>
                    <strong>{study.quote.name}</strong>
                    {study.quote.role}
                  </span>
                </figcaption>
              </figure>
            </div>
          </section>
        )}

        <section className="case-result">
          <div className="container mx-auto case-columns">
            <div>
              <span className="case-label">Aujourd'hui</span>
              <h2>Le résultat</h2>
            </div>
            <ul>
              {study.result.map((r) => (
                <li key={r}>{r}</li>
              ))}
            </ul>
          </div>
        </section>

        <section id="contact" className="web-contact py-24 md:py-32 border-t border-border/30">
          <div className="container mx-auto px-6 max-w-3xl text-center">
            <span className="text-xs font-bold uppercase tracking-widest text-gold mb-4 block">
              Votre projet
            </span>
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-6">
              Votre site et votre fiche Google, pour que vos clients vous trouvent
            </h2>
            <p className="text-muted-foreground mb-4 leading-relaxed">
              Appel découverte de 30 minutes, gratuit et sans engagement. Voir aussi notre offre de{" "}
              <Link to="/services/creation-site" className="text-gold hover:underline">
                création de site
              </Link>
              .
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10">
              <Button variant="hero" size="xl" className="group" asChild>
                <a href={CALENDLY} target="_blank" rel="noopener noreferrer">
                  Réserver mon appel découverte
                  <ArrowUpRight
                    className="w-5 h-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                    aria-hidden="true"
                  />
                </a>
              </Button>
              <Button variant="heroOutline" size="xl" asChild>
                <Link to="/devis">Demander un devis</Link>
              </Button>
            </div>
          </div>
        </section>

        <section className="case-others">
          <div className="container mx-auto">
            <span className="case-label">Autres réalisations</span>
            <ul>
              {others.map((c) => (
                <li key={c.slug}>
                  <Link to={`/realisations/${c.slug}/`}>
                    <img src={c.image} alt="" width="900" height="580" loading="lazy" />
                    <strong>
                      {c.client}
                      <ArrowUpRight className="w-4 h-4" aria-hidden="true" />
                    </strong>
                    <span>{c.sector}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      </main>
    </div>
  );
}
